import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRightLeft, ArrowUpRight, ShieldAlert, Loader2, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { approveDiscount, rejectDiscount, transferApproval, snoozeApproval, APROVACAO_DESCONTO_PERMISSION } from '../lib/discountApproval';
import { getPlano } from '../lib/politicaComercial';
import { resumirPedidoDesconto, type ResumoPedidoDesconto, type PedidoRegistrado } from '../lib/resumoPedidoDesconto';
import { Profile } from '../../types';
import TransferUserModal from '../../views/CRM/components/NegotiationDetails/TransferUserModal';

/** Papéis que hoje têm poder de aprovar/rejeitar desconto (mesmo gate de DetailFeed.tsx) — usado só como destino do "Transferir". */
const ROLES_APROVADORES = ['gerente', 'adm', 'gerente_parcerias'];

/** Permissão que liga o pop-up automático por pessoa (Config → Usuários → Permissões). */
// Reexportada: o PermissionsModal importa daqui. A fonte fica em src/lib/discountApproval.ts.
export { APROVACAO_DESCONTO_PERMISSION };

/** O pop-up automático (diferente do Aprovar/Rejeitar manual em DetailFeed.tsx) é nominal: só quem tem a
 *  permissão acima. Era papel+departamento (gerente do Comercial Externo / adm do Financeiro), mas isso
 *  pegava todo adm do Financeiro — a Meryene recebia pendência de desconto comercial que não é dela
 *  (decisão de 06/08/2026). Quem aprova hoje: Bruno Vieira e Vinicius Alves. */
const podeVerPopup = (p: Profile | null | undefined) => !!p && !!p.permissions?.includes(APROVACAO_DESCONTO_PERMISSION);

interface PendingItem {
    id: number;
    titulo: string;
    cliente_nome: string;
    responsavel_id?: string;
    responsavelNome?: string;
    responsavelRole?: string;
    plano_codigo?: string | null;
    multiplicador_final?: number | null;
    motivo: string;
    /** O que foi pedido (#6125): sem isso o aviso só mostrava "1,20x". */
    resumo: ResumoPedidoDesconto;
}

/** Mesma distinção de papel do badge em views/CRM/Kanban/KanbanCard.tsx (vendedor x parceiro/vendedor_parceiro), aqui como pill com texto. */
const badgePapel = (role?: string) => role === 'vendedor'
    ? { label: 'Vendedor', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' }
    : (role === 'parceiro' || role === 'vendedor_parceiro')
        ? { label: 'Parceiro', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400' }
        : null;

const fmtMult = (v: number) => `${v.toFixed(2).replace('.', ',')}x`;

/** Um valor do pedido: rótulo pequeno em cima, valor embaixo. `alerta` pinta de laranja o que foge da política. */
function Chip({ rotulo, valor, alerta, vazio }: { rotulo: string; valor: string; alerta?: boolean; vazio?: boolean }) {
    return (
        <div className={`rounded-lg border px-2.5 py-1.5 ${alerta && !vazio
            ? 'border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-900/20'
            : 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60'}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">{rotulo}</p>
            <p className={`text-sm font-semibold ${vazio
                ? 'text-gray-400 dark:text-slate-500 font-normal italic'
                : alerta ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-white'}`}>{valor}</p>
        </div>
    );
}

/**
 * Pop-up exibido a gerente/adm/gerente_parcerias ao abrir o sistema, listando
 * toda negociação com desconto_status='pendente' (condição fora da política
 * ou carência não-padrão). Reaproveita a mesma lógica de aprovar/rejeitar/
 * transferir do DetailFeed (src/lib/discountApproval.ts) para não haver dois
 * caminhos divergentes de decisão sobre o mesmo desconto.
 */
const AprovacaoDescontoPopup = () => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const navigate = useNavigate();

    const [pendencias, setPendencias] = useState<PendingItem[]>([]);
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [transferindo, setTransferindo] = useState<PendingItem | null>(null);
    const [reagendando, setReagendando] = useState<PendingItem | null>(null);
    const [diasReagendar, setDiasReagendar] = useState(3);

    const podeAprovar = podeVerPopup(profile);

    const fetchPendencias = useCallback(async () => {
        if (!user || !podeAprovar) return;

        const { data: negs } = await supabase
            .from('negociacoes')
            .select('id, titulo, cliente_nome, responsavel_id, plano_codigo, multiplicador_final, fidelidade')
            .eq('desconto_status', 'pendente');

        if (!negs || negs.length === 0) {
            setPendencias([]);
            return;
        }

        const ids = negs.map(n => n.id);
        const responsavelIds = Array.from(new Set(negs.map(n => n.responsavel_id).filter(Boolean))) as string[];

        const [{ data: notas }, perfisResult, { data: pedidos }] = await Promise.all([
            supabase
                .from('negociacao_notas')
                .select('negociacao_id, conteudo, created_at, user_id')
                .in('negociacao_id', ids)
                .or('conteudo.like.%[APPROVAL_REQUEST]%,conteudo.like.%[APPROVAL_SNOOZE%')
                .order('created_at', { ascending: false }),
            responsavelIds.length > 0
                ? supabase.from('profiles').select('id, full_name, role').in('id', responsavelIds)
                : Promise.resolve({ data: [] as { id: string; full_name: string; role: string }[] }),
            // Registro do pedido (#5917): guarda os percentuais e a carência como foram
            // pedidos. Falhar aqui não pode esconder o aviso — cai no plano da negociação.
            supabase
                .from('aprovacoes_desconto')
                .select('negociacao_id, desconto_tarifa, desconto_bandeira, carencia, motivo, solicitado_em')
                .in('negociacao_id', ids)
                .eq('status', 'pendente')
                .order('solicitado_em', { ascending: false })
                .then(r => (r.error ? { data: [] } : r)),
        ]);
        const pedidoPorNegociacao = new Map<number, PedidoRegistrado & { motivo?: string | null }>();
        (pedidos || []).forEach((p: PedidoRegistrado & { negociacao_id: number; motivo?: string | null }) => {
            // Já vem do mais novo para o mais antigo: o primeiro é o pedido em aberto.
            if (!pedidoPorNegociacao.has(p.negociacao_id)) pedidoPorNegociacao.set(p.negociacao_id, p);
        });

        // Notas já vêm ordenadas por created_at desc: a primeira ocorrência de cada
        // negociacao_id ao iterar já é a mais recente daquela negociação.
        const motivoPorNegociacao = new Map<number, string>();
        const requestCreatedAtPorNegociacao = new Map<number, string>();
        // Snooze só conta se foi o próprio usuário logado quem adiou (decisão: não é global).
        const snoozeAtePorNegociacao = new Map<number, Date>();
        const snoozeCreatedAtPorNegociacao = new Map<number, string>();
        (notas || []).forEach((n: { negociacao_id: number; conteudo: string; created_at: string; user_id: string }) => {
            if (n.conteudo.includes('[APPROVAL_REQUEST]') && !motivoPorNegociacao.has(n.negociacao_id)) {
                motivoPorNegociacao.set(n.negociacao_id, n.conteudo.replace('[APPROVAL_REQUEST] ', ''));
                requestCreatedAtPorNegociacao.set(n.negociacao_id, n.created_at);
            }
            if (n.conteudo.startsWith('[APPROVAL_SNOOZE:') && n.user_id === user.id && !snoozeAtePorNegociacao.has(n.negociacao_id)) {
                const iso = n.conteudo.match(/^\[APPROVAL_SNOOZE:([^\]]+)\]/)?.[1];
                if (iso) {
                    snoozeAtePorNegociacao.set(n.negociacao_id, new Date(iso));
                    snoozeCreatedAtPorNegociacao.set(n.negociacao_id, n.created_at);
                }
            }
        });

        const nomePorId = new Map((perfisResult.data || []).map((p: { id: string; full_name: string }) => [p.id, p.full_name]));
        const rolePorId = new Map((perfisResult.data || []).map((p: { id: string; role: string }) => [p.id, p.role]));

        const visiveis = negs.filter(n => {
            const snoozeAte = snoozeAtePorNegociacao.get(n.id);
            if (!snoozeAte || snoozeAte <= new Date()) return true;
            const snoozeCreatedAt = snoozeCreatedAtPorNegociacao.get(n.id);
            const requestCreatedAt = requestCreatedAtPorNegociacao.get(n.id);
            const snoozeAindaValido = !requestCreatedAt || (snoozeCreatedAt && snoozeCreatedAt > requestCreatedAt);
            return !snoozeAindaValido;
        });

        setPendencias(visiveis.map(n => ({
            id: n.id,
            titulo: n.titulo,
            cliente_nome: n.cliente_nome,
            responsavel_id: n.responsavel_id,
            responsavelNome: n.responsavel_id ? nomePorId.get(n.responsavel_id) : undefined,
            responsavelRole: n.responsavel_id ? rolePorId.get(n.responsavel_id) : undefined,
            plano_codigo: n.plano_codigo,
            multiplicador_final: n.multiplicador_final,
            // A nota [APPROVAL_REQUEST] é editável e some; o registro do pedido guarda o
            // motivo que o sistema montou. Só na falta dos dois vale o texto genérico.
            motivo: motivoPorNegociacao.get(n.id) || pedidoPorNegociacao.get(n.id)?.motivo || 'Condição fora da política comercial.',
            resumo: resumirPedidoDesconto(n, pedidoPorNegociacao.get(n.id)),
        })));
    }, [user, podeAprovar]);

    useEffect(() => { fetchPendencias(); }, [fetchPendencias]);

    const fecharCard = (id: number) => setPendencias(prev => prev.filter(p => p.id !== id));

    const handleAprovar = async (item: PendingItem) => {
        if (!user) return;
        setLoadingId(item.id);
        try {
            await approveDiscount(item, user.id, profile?.full_name);
            fecharCard(item.id);
            addToast('Desconto aprovado.', 'success');
        } catch (e: any) {
            addToast('Erro ao aprovar desconto: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleRejeitar = async (item: PendingItem) => {
        if (!user) return;
        setLoadingId(item.id);
        try {
            await rejectDiscount(item, user.id, profile?.full_name);
            fecharCard(item.id);
            addToast('Desconto rejeitado.', 'success');
        } catch (e: any) {
            addToast('Erro ao rejeitar desconto: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleTransferirSelecionado = async (destino: Profile) => {
        if (!user || !transferindo) return;
        setLoadingId(transferindo.id);
        try {
            await transferApproval(transferindo, user.id, profile?.full_name, destino);
            addToast(`Aprovação transferida para ${destino.full_name}.`, 'success');
            setTransferindo(null);
        } catch (e: any) {
            addToast('Erro ao transferir: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleConfirmarReagendar = async (item: PendingItem) => {
        if (!user) return;
        setLoadingId(item.id);
        try {
            await snoozeApproval(item, user.id, profile?.full_name, diasReagendar);
            fecharCard(item.id);
            addToast(`Adiado por ${diasReagendar} dia${diasReagendar > 1 ? 's' : ''}.`, 'success');
            setReagendando(null);
        } catch (e: any) {
            addToast('Erro ao adiar: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleVerMais = (item: PendingItem) => {
        setPendencias([]);
        navigate(`/crm?negociacaoId=${item.id}`);
    };

    if (!podeAprovar || pendencias.length === 0) return null;

    return (
        <>
            <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-6 py-5 border-b border-orange-200 dark:border-orange-800/40 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 shrink-0">
                        <div className="p-2.5 bg-orange-100 dark:bg-orange-900/40 rounded-xl">
                            <ShieldAlert size={20} className="text-orange-600" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-orange-900 dark:text-orange-300">
                                {pendencias.length === 1 ? '1 desconto aguardando aprovação' : `${pendencias.length} descontos aguardando aprovação`}
                            </h3>
                            <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Revise as condições solicitadas abaixo.</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="overflow-y-auto custom-scrollbar p-6 space-y-4">
                        {pendencias.map(item => {
                            const plano = getPlano(item.plano_codigo);
                            const busy = loadingId === item.id;
                            const papel = badgePapel(item.responsavelRole);
                            return (
                                <div key={item.id} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                                {item.cliente_nome || item.titulo}
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                                Solicitado por {item.responsavelNome || 'vendedor'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {papel && (
                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${papel.className}`}>
                                                    {papel.label}
                                                </span>
                                            )}
                                            {item.multiplicador_final != null && (
                                                <span title="Multiplicador da comissão para este plano e fidelidade — quanto maior, mais caro o desconto para a Vigor." className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold font-mono cursor-help ${
                                                    plano?.foraDaPolitica
                                                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                                                        : 'bg-primary/10 text-primary'
                                                }`}>
                                                    {fmtMult(item.multiplicador_final)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* O que está sendo pedido (#6125). Laranja = fora da política. */}
                                    <div className="flex flex-wrap gap-2"
                                        title={item.resumo.fonte === 'plano'
                                            ? 'Percentuais do plano da negociação (pedido feito antes de o sistema registrar os valores de cada pedido).'
                                            : undefined}>
                                        {item.resumo.plano ? (
                                            <Chip rotulo="Plano" valor={item.resumo.plano} alerta={item.resumo.foraDaPolitica} />
                                        ) : null}
                                        <Chip rotulo="Tarifa" valor={item.resumo.tarifa ?? 'não informado'} alerta={item.resumo.foraDaPolitica} vazio={!item.resumo.tarifa} />
                                        <Chip rotulo="Bandeira" valor={item.resumo.bandeira ?? 'não informado'} alerta={item.resumo.foraDaPolitica} vazio={!item.resumo.bandeira} />
                                        <Chip rotulo="Carência" valor={item.resumo.carencia ?? 'sem carência'} alerta={!!item.resumo.carencia} vazio={!item.resumo.carencia} />
                                        {item.resumo.fidelidade ? <Chip rotulo="Fidelidade" valor={item.resumo.fidelidade} /> : null}
                                    </div>

                                    <p className="text-sm text-gray-700 dark:text-slate-300">{item.motivo}</p>

                                    {reagendando?.id === item.id ? (
                                        <div className="flex flex-wrap items-center gap-2 pt-1">
                                            <label className="text-xs text-gray-600 dark:text-slate-300">Adiar por</label>
                                            <select
                                                value={diasReagendar}
                                                onChange={e => setDiasReagendar(Number(e.target.value))}
                                                className="border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                                            >
                                                {Array.from({ length: 10 }, (_, i) => i + 1).map(d => (
                                                    <option key={d} value={d}>{d} dia{d > 1 ? 's' : ''}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => setReagendando(null)}
                                                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleConfirmarReagendar(item)}
                                                disabled={busy}
                                                className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                            >
                                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirmar
                                            </button>
                                        </div>
                                    ) : (
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => handleVerMais(item)}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                                        >
                                            <ArrowUpRight size={13} /> Ver mais
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTransferindo(item)}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <ArrowRightLeft size={13} /> Transferir
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setReagendando(item); setDiasReagendar(3); }}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <Clock size={13} /> Reagendar
                                        </button>
                                        <div className="flex-1" />
                                        <button
                                            type="button"
                                            onClick={() => handleRejeitar(item)}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            {busy ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Rejeitar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAprovar(item)}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Aprovar
                                        </button>
                                    </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <TransferUserModal
                isOpen={!!transferindo}
                onClose={() => setTransferindo(null)}
                onSelect={handleTransferirSelecionado}
                currentResponsibleId={user?.id}
                roleFilter={ROLES_APROVADORES}
                title="Transferir Aprovação"
            />
        </>
    );
};

export default AprovacaoDescontoPopup;
